// [CASH-MODE] Cash Transactions report (super admin): cash payments recorded by shops.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { customerNoOf } from '../../components/CustomerReportShell';
import { formatDateTimeIST } from '../../utils/dateFormat';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';

const toInputDate = (d) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().split('T')[0];
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(Number(amount) || 0);

const formatDateTime = (value) => formatDateTimeIST(value);

const csvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

const statusOf = (row) => {
  if (row.commissionProcessed && row.gamePointsProcessed !== false) return { label: 'Processed', color: 'success' };
  if (row.commissionProcessed) return { label: 'Game pending', color: 'warning' };
  if (row.processingError) return { label: 'Failed', color: 'error' };
  return { label: 'Unprocessed', color: 'warning' };
};

const EMPTY_TOTALS = { count: 0, amount: 0, unprocessed: 0 };

const CashTransactions = () => {
  const today = new Date();
  const weekAgo = new Date();
  weekAgo.setDate(today.getDate() - 7);

  const { can } = usePermissions();
  const [startDate, setStartDate] = useState(toInputDate(weekAgo));
  const [endDate, setEndDate] = useState(toInputDate(today));
  const [search, setSearch] = useState('');
  const [shopId, setShopId] = useState('');
  const [processed, setProcessed] = useState('all'); // 'all' | 'true' | 'false'
  const [applied, setApplied] = useState(null); // filters used for the current result
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const buildParams = useCallback((filters, pageNo, limit) => {
    const params = { startDate: filters.startDate, endDate: filters.endDate, page: pageNo, limit };
    if (filters.search.trim()) params.search = filters.search.trim();
    if (filters.shopId.trim()) params.shopId = filters.shopId.trim();
    if (filters.processed !== 'all') params.processed = filters.processed;
    return params;
  }, []);

  const fetchRows = useCallback(async (filters, pageIndex, limit) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/cash/admin/transactions`, {
        params: buildParams(filters, pageIndex + 1, limit),
        headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
      });
      const data = response.data || {};
      setRows(Array.isArray(data.data) ? data.data : []);
      setTotals(data.totals || EMPTY_TOTALS);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      setRows([]);
      setTotals(EMPTY_TOTALS);
      setTotal(0);
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to load cash transactions', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const handleApply = () => {
    if (!startDate || !endDate) {
      setSnackbar({ open: true, message: 'Select both start and end dates', severity: 'warning' });
      return;
    }
    if (startDate > endDate) {
      setSnackbar({ open: true, message: 'Start date cannot be after end date', severity: 'warning' });
      return;
    }
    setPage(0);
    setApplied({ startDate, endDate, search, shopId, processed });
  };

  // First load (page access is enforced by the router permission guard)
  useEffect(() => {
    if (!applied) {
      setApplied({ startDate, endDate, search, shopId, processed });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (applied) fetchRows(applied, page, rowsPerPage);
  }, [applied, page, rowsPerPage, fetchRows]);

  const handleExportCsv = async () => {
    if (!can('reports.cash.export')) return;
    if (!applied) return;
    setExporting(true);
    try {
      const all = [];
      const limit = 500;
      let pageNo = 1;
      let pages = 1;
      do {
        // eslint-disable-next-line no-await-in-loop
        const response = await axios.get(`${API_BASE_URL}/cash/admin/transactions`, {
          params: buildParams(applied, pageNo, limit),
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        });
        const data = response.data || {};
        all.push(...(Array.isArray(data.data) ? data.data : []));
        pages = data.pagination?.pages || 1;
        pageNo += 1;
      } while (pageNo <= pages);

      if (all.length === 0) {
        setSnackbar({ open: true, message: 'No data to export', severity: 'warning' });
        return;
      }
      const headers = ['S.No', 'Transaction No', 'Customer ID', 'Payer Name', 'Amount', 'Shop Name', 'Shop Code', 'State Head ID', 'State Staff ID', 'Q-Pay Centre ID', 'Q-Pay Agent ID', 'Date & Time', 'Commission Processed', 'Game Points Processed', 'Error', 'Mode'];
      const lines = [headers.join(',')];
      all.forEach((row, i) => {
        lines.push([
          i + 1,
          csvCell(row.txnNo || '—'),
          csvCell(customerNoOf(row)),
          csvCell(row.payerName),
          csvCell(Number(row.amount || 0).toFixed(2)),
          csvCell(row.shopName),
          csvCell(row.shopCode),
          csvCell(row.stateHeadId),
          csvCell(row.stateStaffId),
          csvCell(row.qpayCentreId),
          csvCell(row.qpayAgentId),
          csvCell(formatDateTime(row.transactionDate)),
          csvCell(row.commissionProcessed ? 'Yes' : 'No'),
          csvCell(row.gamePointsProcessed ? 'Yes' : 'No'),
          csvCell(row.processingError),
          csvCell('Cash'),
        ].join(','));
      });
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cash_transactions_${applied.startDate}_to_${applied.endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to export CSV', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const summaryCards = [
    { label: 'Cash Payments', value: totals.count || 0, color: 'primary.main' },
    { label: 'Cash Amount', value: formatCurrency(totals.amount), color: 'warning.main' },
    { label: 'Unprocessed', value: totals.unprocessed || 0, color: (totals.unprocessed || 0) > 0 ? 'error.main' : 'success.main' },
  ];

  return (
    <Box sx={{ position: 'relative', padding: 2 }}>
      <BackButton />
      <br /><br />
      <Box sx={{ width: '100%', p: { xs: 0, md: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Typography variant="h4">Cash Transactions</Typography>
          {can('reports.cash.export') && (
            <Button
              variant="contained"
              color="success"
              startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
              onClick={handleExportCsv}
              disabled={exporting || loading || total === 0}
            >
              Export CSV
            </Button>
          )}
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summaryCards.map((card) => (
            <Grid item xs={12} sm={4} key={card.label}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ color: card.color }}>{card.label}</Typography>
                  <Typography variant="h5" fontWeight="bold">{card.value}</Typography>
                  <Typography variant="caption" color="text.secondary">For selected filters</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Search"
                placeholder="Transaction No, payer or shop name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" label="Shop ID (optional)" value={shopId} onChange={(e) => setShopId(e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="cash-processed-label">Status</InputLabel>
                <Select labelId="cash-processed-label" value={processed} label="Status" onChange={(e) => setProcessed(e.target.value)}>
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="true">Processed</MenuItem>
                  <MenuItem value="false">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Button fullWidth variant="contained" startIcon={<FilterListIcon />} onClick={handleApply} disabled={loading}>
                Apply
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small" sx={{ minWidth: 1300 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1e293b' }}>
                  {['S.No', 'Transaction No', 'Customer ID', 'Payer Name', 'Amount', 'Shop', 'State Head ID', 'State Staff ID', 'Q-Pay Centre ID', 'Q-Pay Agent ID', 'Date & Time', 'Status', 'Error'].map((h) => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No cash transactions found for the selected filters.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => {
                    const status = statusOf(row);
                    return (
                      <TableRow key={row.refId || row._id || idx} hover>
                        <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.txnNo || '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{customerNoOf(row)}</TableCell>
                        <TableCell>{row.payerName || 'N/A'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{formatCurrency(row.amount)}</TableCell>
                        <TableCell>
                          {row.shopName || 'N/A'}
                          {row.shopCode && (
                            <Typography variant="caption" color="text.secondary" display="block">{row.shopCode}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.stateHeadId || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.stateStaffId || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.qpayCentreId || 'N/A'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{row.qpayAgentId || 'N/A'}</TableCell>
                        <TableCell>{formatDateTime(row.transactionDate)}</TableCell>
                        <TableCell><Chip label={status.label} color={status.color} size="small" /></TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          {row.processingError ? (
                            <Tooltip title={row.processingError}>
                              <Typography variant="caption" color="error" noWrap display="block">{row.processingError}</Typography>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CashTransactions;
